import React from 'react';
import { Link } from 'react-router-dom';
import { Mountain, Leaf, Droplets, Users, Lightbulb, BookOpen, Globe, Heart, ArrowRight, MessageCircle, CheckCircle, Handshake, TreePine, Download, Sun, Wifi, Recycle, ShieldCheck } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

const GREEN = '#2D6A4F';
const EARTH = '#A67C52';
const SKY = '#4A9EC7';

const PILLARS = [
  {
    icon: TreePine,
    title: 'Sustainable Tourism',
    color: GREEN,
    desc: 'Building responsible tourism infrastructure across the Himalayan belt — waste management systems, eco-practice certifications for homestays, and traveller awareness programs.',
    initiatives: [
      'Waste segregation and composting systems for partner homestays',
      'Single-use plastic elimination program across GoHimalayan stays',
      'Responsible Traveller Guidelines distributed to every booking',
      'Trail maintenance and cleanup drives on popular trekking routes',
    ],
    partners: ['Zero Waste Himalaya Alliance', 'Waste Warriors', 'Healing Himalayas Foundation'],
  },
  {
    icon: Users,
    title: 'Capacity Building',
    color: SKY,
    desc: 'Empowering Himalayan homestay hosts with digital skills, hospitality training, and direct market access — reducing dependency on OTAs and middlemen.',
    initiatives: [
      'Digital literacy workshops for homestay owners (listing, photography, pricing)',
      'AI-powered content creation for host properties (proven at scale — our pipeline)',
      'Hospitality and guest experience training programs',
      'Financial literacy and direct booking management',
    ],
    partners: ['Aarohi Society', 'Himmotthan Society', 'CHIRAG Kumaon'],
  },
  {
    icon: Heart,
    title: 'Livelihood & Artisan Support',
    color: EARTH,
    desc: 'Connecting mountain artisans, weavers, and food producers to national and international markets through the GoHimalayan Store — fair prices, transparent sourcing.',
    initiatives: [
      'GoHimalayan Store — curated marketplace for Himalayan artisan products',
      'Direct artisan-to-consumer supply chains (no middlemen)',
      'Packaging and branding support for village-level producers',
      'International market access for Himalayan crafts and food',
    ],
    partners: ['Looms of Ladakh', 'Kilmora (CHIRAG)', 'Avani Kumaon', 'Bhuira Jams', 'Pahari Haat'],
  },
  {
    icon: Droplets,
    title: 'Water & Ecology',
    color: SKY,
    desc: 'Supporting springshed revival, forest conservation, and watershed management in Himalayan communities where tourism and ecology must coexist.',
    initiatives: [
      'Springshed revival programs in water-stressed mountain villages',
      'Reforestation drives with native Himalayan species (oak, rhododendron, deodar)',
      'Wetland and river ecosystem monitoring near tourism zones',
      'Community-managed water harvesting systems for homestays',
    ],
    partners: ['IISD Ladakh', 'Himmotthan Society', 'Himalayan Eco Tourism'],
  },
  {
    icon: Lightbulb,
    title: 'Mountain Innovation',
    color: GREEN,
    desc: 'Bringing appropriate technology to remote Himalayan stays — solar power, EV infrastructure, connectivity, and climate-resilient construction.',
    initiatives: [
      'Solar power systems for off-grid homestays',
      'EV charging stations at mountain stays along popular routes',
      'Low-bandwidth digital tools for remote host management',
      'Climate-resilient construction training using local materials',
    ],
    partners: ['Avani Kumaon (solar)', 'Local engineering colleges', 'IISD Ladakh'],
  },
];

const MOAT_POINTS = [
  'Direct relationships with 100+ homestay hosts across 13 Himalayan states',
  'AI content pipeline that can onboard and digitize 1,000+ rural stays per week',
  'Existing partnerships with state tourism departments (Himachal, Uttarakhand, Meghalaya)',
  'GoHimalayan Store providing market access for artisan products',
  'Ground-level connections with last-mile organizations in every Himalayan region',
  'Technology infrastructure (search, analytics, social media) already built and proven',
];

const CSR_TARGETS = [
  { name: 'Tata Trusts', focus: 'Himalayan agriculture, livelihoods, springshed revival', active: true },
  { name: 'Infosys Foundation', focus: 'Rural development, education, healthcare', active: false },
  { name: 'Wipro Foundation', focus: 'Ecological sustainability, community development', active: false },
  { name: 'Axis Bank Foundation', focus: 'Sustainable livelihoods, skill development', active: false },
  { name: 'HCL Foundation', focus: 'Environment, community development, education', active: false },
  { name: 'Mahindra Rise CSR', focus: 'Rural transformation, sustainability', active: false },
  { name: 'Standard Chartered', focus: 'Community engagement, sustainability', active: false },
  { name: 'Godrej Good & Green', focus: 'Environment, livelihoods, skills', active: false },
];

const GROUND_PARTNERS = [
  { name: 'Aarohi Society', region: 'Kumaon, Uttarakhand', focus: 'Community development, innovative tourism', url: null },
  { name: 'CHIRAG', region: 'Kumaon, Uttarakhand', focus: 'Rural enterprise, artisan support (Kilmora)', url: 'kilmora.in' },
  { name: 'Himmotthan Society', region: 'Pan-Uttarakhand', focus: 'Livelihoods, agriculture, water (Tata Trusts partner)', url: null },
  { name: 'Avani Kumaon', region: 'Kumaon, Uttarakhand', focus: 'Handlooms, natural dyes, solar, women artisans', url: 'avani-kumaon.org' },
  { name: 'Looms of Ladakh', region: 'Ladakh', focus: 'Pashmina & yak wool cooperative, 200+ women artisans', url: 'loomsofladakh.in' },
  { name: 'IISD Ladakh', region: 'Ladakh', focus: 'Sustainable eco-tourism, knowledge centre', url: null },
  { name: 'Himalayan Eco Tourism', region: 'Tirthan Valley, HP', focus: 'Eco-stays, GHNP conservation, artisan products', url: 'himalayanecotourism.com' },
  { name: 'Zero Waste Himalaya Alliance', region: 'Pan-Himalayan', focus: 'Plastic waste management, recycling infrastructure', url: null },
  { name: 'Waste Warriors', region: 'Himachal Pradesh', focus: 'Waste management, community cleanups', url: null },
  { name: 'Healing Himalayas Foundation', region: 'Himachal Pradesh', focus: 'Trail cleanup, environmental awareness', url: null },
];

export default function Impact() {
  const whatsappText = encodeURIComponent(`Hi! I'd like to discuss a CSR partnership with GoHimalayan Foundation for Himalayan impact programs.\n\ngohimalayan.com/impact`);
  const whatsappUrl = `https://wa.me/919871155963?text=${whatsappText}`;

  return (
    <div className="min-h-screen bg-white" data-testid="impact-page">
      <div className="fixed top-6 right-6 z-50 print:hidden">
        <button onClick={() => window.print()} className="flex items-center gap-2 px-5 py-2.5 bg-white/90 backdrop-blur-md border border-stone-200 rounded-full shadow-lg text-stone-700 text-xs font-medium tracking-wider uppercase hover:bg-white hover:shadow-xl transition-all" data-testid="download-pdf-btn"><Download className="w-4 h-4" />Download PDF</button>
      </div>

      {/* HERO */}
      <section className="min-h-screen flex flex-col justify-center items-center px-6 relative overflow-hidden" style={{ background: `linear-gradient(160deg, ${GREEN} 0%, #1a3a2a 40%, ${EARTH}40 100%)` }}>
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="absolute bottom-0 left-0 right-0 opacity-10">
          <svg viewBox="0 0 1440 200" preserveAspectRatio="none" className="w-full" fill="white"><path d="M0,200 L0,140 L120,130 L240,150 L360,90 L480,120 L600,60 L720,100 L840,40 L960,80 L1080,50 L1200,70 L1320,45 L1440,60 L1440,200Z" /></svg>
        </div>
        <div className="relative z-10 text-center max-w-4xl">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Mountain className="w-10 h-10 text-white/80" strokeWidth={2} />
            <div className="text-left">
              <span className="text-2xl font-bold text-white/90 tracking-tight block" style={pf}>GoHimalayan</span>
              <span className="text-xs tracking-[0.3em] uppercase text-white/40">Foundation</span>
            </div>
          </div>
          <div className="inline-block px-5 py-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm mb-8">
            <p className="text-white/60 text-xs tracking-[0.3em] uppercase">Sustainability · Capacity Building · Last-Mile Impact</p>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-light italic leading-tight mb-6" style={pf}>The mountains gave us<br />everything.<br />Time to give back.</h1>
          <p className="text-white/75 text-lg max-w-2xl mx-auto mb-12 leading-relaxed" style={pf}>GoHimalayan Foundation works with last-mile organizations across the Indian Himalayas — building sustainable tourism, empowering hosts, supporting artisans, and protecting the ecosystems that make it all possible.</p>
          <div className="flex items-center justify-center gap-6 text-white/40 text-xs tracking-widest uppercase"><span>CSR Partnership Proposal</span><div className="w-1 h-1 rounded-full bg-white/40" /><span>2026</span><div className="w-1 h-1 rounded-full bg-white/40" /><span>gohimalayan.com</span></div>
        </div>
      </section>

      {/* THE MOAT */}
      <section className="py-24 px-6" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: GREEN }}>Why GoHimalayan</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>We don't parachute in.<br />We're already there.</h2>
          <p className="text-stone-500 text-lg max-w-3xl mb-6" style={pf}>Most CSR programs struggle with last-mile reach in the Himalayas. GoHimalayan already has the infrastructure — host relationships across 13 states, an AI content pipeline, state tourism partnerships, and ground-level connections with the organizations doing the real work. We're the distribution layer for Himalayan impact.</p>

          <div className="grid sm:grid-cols-2 gap-3 mt-12">
            {MOAT_POINTS.map((point, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-stone-100">
                <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: GREEN }} />
                <p className="text-sm text-stone-700">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: EARTH }}>Five Pillars</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>Where the impact happens.</h2>
          <p className="text-stone-500 text-lg max-w-2xl mb-16" style={pf}>Five interconnected programs — each with measurable outcomes, ground-level partners, and a clear path from funding to impact.</p>

          <div className="space-y-8">
            {PILLARS.map((p, i) => (
              <div key={i} className="rounded-2xl border border-stone-100 overflow-hidden hover:shadow-md transition-shadow">
                <div className="flex flex-col lg:flex-row">
                  <div className="lg:w-1/3 p-8 flex flex-col justify-center" style={{ backgroundColor: `${p.color}08` }}>
                    <p.icon className="w-8 h-8 mb-4" style={{ color: p.color }} />
                    <h3 className="text-xl font-bold text-stone-900 mb-2" style={pf}>{p.title}</h3>
                    <p className="text-sm text-stone-500 leading-relaxed">{p.desc}</p>
                  </div>
                  <div className="lg:w-2/3 p-8 border-l border-stone-100">
                    <p className="text-xs uppercase tracking-wider text-stone-400 mb-4 font-semibold">Key Initiatives</p>
                    <ul className="space-y-2.5 mb-6">
                      {p.initiatives.map((init, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-stone-600">
                          <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: p.color }} />{init}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs uppercase tracking-wider text-stone-400 mb-2 font-semibold">Ground-Level Partners</p>
                    <div className="flex flex-wrap gap-1.5">
                      {p.partners.map((partner, j) => (
                        <span key={j} className="text-[10px] px-3 py-1.5 rounded-full border" style={{ color: p.color, borderColor: `${p.color}25`, backgroundColor: `${p.color}06` }}>{partner}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GROUND PARTNERS */}
      <section className="py-24 px-6" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: GREEN }}>Last-Mile Network</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-4" style={pf}>The organizations doing the real work.<br />We connect them to capital.</h2>
          <p className="text-stone-500 text-lg max-w-2xl mb-16" style={pf}>These are verified, ground-level organizations operating in the Himalayan belt. GoHimalayan Foundation acts as the bridge between their work and CSR funding.</p>

          <div className="grid sm:grid-cols-2 gap-4">
            {GROUND_PARTNERS.map((gp, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-stone-100 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-base font-semibold text-stone-900" style={pf}>{gp.name}</h3>
                  <span className="text-[9px] text-stone-400 bg-stone-50 px-2 py-0.5 rounded-full flex-shrink-0 ml-2">{gp.region}</span>
                </div>
                <p className="text-sm text-stone-500 mb-2">{gp.focus}</p>
                {gp.url && <p className="text-[10px] text-stone-400">{gp.url}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CSR TARGETS */}
      <section className="py-24 px-6" style={{ background: `linear-gradient(160deg, ${GREEN} 0%, #1a3a2a 40%, #0f1e15 100%)` }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: `${EARTH}` }}>For CSR Partners</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-white mb-4" style={pf}>Your CSR budget.<br />Verified Himalayan impact.<br />GoHimalayan as the bridge.</h2>
          <p className="text-white/40 text-lg max-w-2xl mb-16" style={pf}>We work with corporate foundations looking for measurable, on-the-ground impact in the Indian Himalayas. GoHimalayan Foundation provides the last-mile reach, the local partners, and the reporting infrastructure.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {CSR_TARGETS.map((csr, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm hover:bg-white/8 transition-colors">
                <h3 className="text-white text-sm font-semibold mb-1">{csr.name}</h3>
                <p className="text-white/35 text-xs">{csr.focus}</p>
              </div>
            ))}
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
            <h3 className="text-white text-lg font-semibold mb-4" style={pf}>What a CSR partnership looks like</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'GoHimalayan identifies high-impact projects across its Himalayan network',
                'CSR partner funds specific initiatives (solar for 50 homestays, waste systems for a valley, artisan training)',
                'Ground-level partner organizations execute with GoHimalayan coordination',
                'Quarterly impact reports with photos, metrics, and beneficiary stories',
                'Brand visibility on GoHimalayan platform — state pages, impact stories, social media',
                'Annual site visits organized for CSR teams to see impact firsthand',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${GREEN}30` }}>
                    <span className="text-[10px] font-bold text-white/70">{i + 1}</span>
                  </div>
                  <p className="text-white/50 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT METRICS */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: EARTH }}>Measurable Outcomes</p>
          <h2 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-16" style={pf}>What ₹1 Cr of CSR funding can achieve<br />through GoHimalayan Foundation.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: '200+', label: 'Homestays digitized & trained', icon: Wifi },
              { value: '50', label: 'Solar-powered mountain stays', icon: Sun },
              { value: '500+', label: 'Artisan livelihoods supported', icon: Heart },
              { value: '10,000 kg', label: 'Waste diverted from landfills', icon: Recycle },
            ].map((m, i) => (
              <div key={i} className="p-6 rounded-xl border border-stone-100">
                <m.icon className="w-6 h-6 mx-auto mb-3" style={{ color: i % 2 === 0 ? GREEN : EARTH }} />
                <p className="text-3xl font-bold text-stone-900 mb-1" style={pf}>{m.value}</p>
                <p className="text-sm text-stone-500">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="min-h-[60vh] flex flex-col justify-center items-center px-6" style={{ background: `linear-gradient(180deg, ${GREEN} 0%, #1a3a2a 50%, ${EARTH}80 100%)` }}>
        <div className="text-center max-w-3xl">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Mountain className="w-10 h-10 text-white/30" strokeWidth={1.5} />
            <div className="text-left">
              <span className="text-lg font-bold text-white/50 block" style={pf}>GoHimalayan</span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/25">Foundation</span>
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl font-light italic text-white mb-6" style={pf}>The Himalayas don't need more tourists.<br />They need better partners.</h2>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-stone-900 text-sm font-bold px-8 py-4 rounded-full hover:bg-white/90 transition-colors" data-testid="final-cta">
            <MessageCircle className="w-4 h-4" />Discuss a Partnership
          </a>
          <div className="mt-8">
            <p className="text-white/30 text-xs tracking-wider">foundation@gohimalayan.com · +91 9871155963</p>
          </div>
          <p className="text-white/10 text-xs tracking-wider mt-6">gohimalayan.com/impact · Confidential</p>
        </div>
      </section>
    </div>
  );
}
